import React from 'react';
import { CircuitRenderer, CircuitElement } from './CircuitRenderer';

interface VoltageDividerDiagramProps {
    vIn: number;
    r1: number;
    r2: number;
    vOut: number;
    solveFor: 'vIn' | 'r1' | 'r2' | 'vOut';
}

export const VoltageDividerDiagram: React.FC<VoltageDividerDiagramProps> = ({
    vIn,
    r1,
    r2,
    vOut,
    solveFor
}) => {
    // Layout constants
    const width = 450;
    const height = 350;
    const padding = 50;

    const leftX = padding + 40; // 90
    const rightX = width - padding - 60; // 340
    const topY = padding + 20; // 70
    const bottomY = height - padding - 20; // 280
    const midY = (topY + bottomY) / 2;

    const highlightColor = "#2563eb";

    const elements: CircuitElement[] = [
        // Top Wire
        {
            type: 'wire',
            from: { x: leftX, y: topY },
            to: { x: rightX, y: topY },
            labelSide: 'left'
        },

        // Right Loop - R1 (Top half)
        {
            type: 'resistor',
            from: { x: rightX, y: topY },
            to: { x: rightX, y: midY },
            value: r1,
            label: solveFor === 'r1' ? 'R1 = ?' : `R1 = ${r1}Ω`,
            labelColor: solveFor === 'r1' ? highlightColor : undefined,
            labelSide: 'left'
        },

        // Right Loop - R2 (Bottom half)
        {
            type: 'resistor',
            from: { x: rightX, y: midY },
            to: { x: rightX, y: bottomY },
            value: r2,
            label: solveFor === 'r2' ? 'R2 = ?' : `R2 = ${r2}Ω`,
            labelColor: solveFor === 'r2' ? highlightColor : undefined,
            labelSide: 'left'
        },

        // Bottom Wire
        {
            type: 'wire',
            from: { x: rightX, y: bottomY },
            to: { x: leftX, y: bottomY },
            labelSide: 'left'
        },

        // Voltage Source
        {
            type: 'voltageSource',
            from: { x: leftX, y: bottomY },
            to: { x: leftX, y: topY },
            value: vIn,
            label: solveFor === 'vIn' ? 'Vin = ?' : `Vin = ${vIn}V`,
            labelColor: solveFor === 'vIn' ? highlightColor : undefined,
            labelSide: 'left'
        },

        // Vout tap wire
        {
            type: 'wire',
            from: { x: rightX, y: midY },
            to: { x: rightX + 40, y: midY },
        }
    ];

    // Vout Label - attached to junction at tap point
    const junctions = [
        {
            x: rightX + 40,
            y: midY,
            label: solveFor === 'vOut' ? 'Vout = ?' : `Vout = ${vOut}V`,
            labelPosition: 'right' as const,
            labelOffset: 30
        }
    ];

    return (
        <CircuitRenderer
            width={width}
            height={height}
            elements={elements}
            junctions={junctions}
        />
    );
};
